import { gdprCategories, gdprDataSubjectCategories, gdprDeletionCriteria, gdprDeletionPeriod, gdprLegalBases, gdprProcessingPurposes, gdprRecipientCategories, gdprRiskLevels, gdprTechnicalMeasures, gdprThirdCountries, gdprThirdCountryGuarantees } from '../../../lib/data/gdpr-data';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { DataCategory, DataSubjectCategory, deletionPeriod, isoCountryCodes, LegalBasis, ProcessingActivity, Recipient, RecipientCategory, ThirdCountryGuarantees } from '../../../lib/interfaces/gdpr-processing-activities';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserdataService } from '../../../service/userdata.service';
import { DeletionCriteria } from '../../../interfaces/gdpr-processing-activities';
import { GdprService } from '../../../service/gdpr.service';

@Component({
    selector: 'app-add-processing-activities',
    standalone: false,
    templateUrl: './add-processing-activities.component.html',
    styleUrl: './add-processing-activities.component.scss'
})
export class AddProcessingActivitiesComponent implements OnInit {
    private fb = inject(FormBuilder);
    private dialog = inject(MatDialogRef<AddProcessingActivitiesComponent>);
    protected data = inject(MAT_DIALOG_DATA);
    private userService = inject(UserdataService);
    private gdprService = inject(GdprService);

    protected isNew: boolean = true;
    protected _gdprCategories = gdprCategories;
    protected _gdprLegalBases = gdprLegalBases;
    protected _gdprTechnicalMeasures = gdprTechnicalMeasures;
    protected _gdprProcessingPurposes = gdprProcessingPurposes;
    protected _gdprDataSubjectCategories = gdprDataSubjectCategories;
    protected _gdprRecipientCategories = gdprRecipientCategories;
    protected _gdprThirdCountryGuarantees = gdprThirdCountryGuarantees;
    protected _gdprThirdCountries = gdprThirdCountries;
    protected _gdprDeletionPeriods = gdprDeletionPeriod;
    protected _gdprDeletionCriteria = gdprDeletionCriteria;
    protected _gdprRiskLevels = gdprRiskLevels;

    protected selectableGdprThirdCountryGuarantees: { value: ThirdCountryGuarantees; label: string; selected: boolean; }[] = [];
    protected selectableGdprCategories: { value: DataCategory; label: string; selected: boolean; }[] = [];
    protected selectableGdprDataSubjectCategories: { value: DataSubjectCategory; label: string; selected: boolean; }[] = [];
    protected selectableGdprDeletionPeriods: { value: deletionPeriod; label: string; selected: boolean; }[] = [];
    protected selectableGdprDeletionCriteria: { value: DeletionCriteria; label: string; selected: boolean; }[] = [];
    protected LegalBasis = LegalBasis;

    protected status = [
        { value: 'active', label: 'Aktiv' },
        { value: 'inactive', label: 'Inaktiv' },
        { value: 'draft', label: 'Entwurf' }
    ];

    protected newRecipient = this.fb.group({
        name: ['', Validators.required],
        category: [RecipientCategory.OTHER, Validators.required],
        country: [''],
        isThirdCountry: [false, Validators.required],
        thirdCountryGuarantees: [''],
        purpose: ['', Validators.required],
    });

    protected activityFormData = this.fb.group({
        id: [],
        controllerName: ['', Validators.required],
        controllerContact: ['', Validators.required],
        jointControllerName: [''],
        jointControllerContact: [''],
        representativeName: [''],
        representativeContact: [''],
        dpoName: [''],
        dpoContact: [''],
        processingPurpose: ['', Validators.required],
        processingDescription: ['', Validators.required],
        legalBasis: ['', Validators.required],
        legitimateInterestDescription: [''],
        dataSubjectCategories: [[] as DataSubjectCategory[], Validators.required],
        dataCategories: [[], Validators.required],
        isSpecialCategory: [false, Validators.required],
        isCriminalData: [false, Validators.required],
        recipients: [[] as Recipient[], Validators.required],
        deletionPeriod: [[] as deletionPeriod[], Validators.required],
        deletionCriteria: ['', Validators.required],
        technicalMeasures: this.fb.group({
            backup: [false],
            training: [false],
            encryption: [false],
            accessControl: [false],
            securityTesting: [false],
            dataMinimization: [false],
            incidentResponse: [false],
            pseudonymization: [false],
        }),
        isRegularProcessing: [false, Validators.required],
        riskAssessment: ['', Validators.required],
        dataProtectionImpactAssessment: [false, Validators.required],
        status: ['draft', Validators.required],
    });

    ngOnInit() {
        console.log("Data", this.data);

        if (this.data && this.data.activity) {
            this.isNew = false;
            this.activityFormData.patchValue(this.data.activity);
        } else {
            // Set default controllerName and controllerContact from userService
            this.activityFormData.patchValue({
                controllerName: this.userService.getUserDataByKey('prename') + ' ' + this.userService.getUserDataByKey('lastname'),
                controllerContact: this.userService.getUserDataByKey('email')
            });
        }

        this.expandObject(this._gdprDataSubjectCategories, this.selectableGdprDataSubjectCategories);
        this.expandObject(this._gdprCategories, this.selectableGdprCategories);
        this.expandObject(this._gdprThirdCountryGuarantees, this.selectableGdprThirdCountryGuarantees);
        this.expandObject(this._gdprDeletionPeriods, this.selectableGdprDeletionPeriods);
        this.expandObject(this._gdprDeletionCriteria, this.selectableGdprDeletionCriteria);
    }

    protected isThirdCountry(value: isoCountryCodes): void {
        let c = this._gdprThirdCountries.find(tc => tc.value === value);
        if (c) {
            this.newRecipient.get('isThirdCountry')?.setValue(c.isThirdCountry);
        }
    }

    private expandObject(sourceData: any[], targetData: any[]): void {
        sourceData.forEach(sd => {
            let keys = Object.keys(sd);
            let objToTarget: any = { selected: false };
            for (let key of keys) {
                objToTarget[key] = sd[key];
            }
            targetData.push(objToTarget);
        });
    }
    protected addRecipient(): void {
        let currentRecipients: Recipient[] = this.activityFormData.get('recipients')?.value || [];
        currentRecipients.push(this.newRecipient.value as Recipient);
        this.activityFormData.get('recipients')?.setValue(currentRecipients as any);
        this.newRecipient.reset({
            name: '',
            category: RecipientCategory.OTHER,
            isThirdCountry: false,
            purpose: ''
        });
    }
    protected removeRecipient(recipient: Recipient): void {
        let currentRecipients: Recipient[] = this.activityFormData.get('recipients')?.value || [];
        currentRecipients = currentRecipients.filter(r => r !== recipient);
        this.activityFormData.get('recipients')?.setValue(currentRecipients as any);
    }
    protected async toggleCat(catValue: any, formtarget: string): Promise<void> {
        let currentCategories: DataSubjectCategory[] = this.activityFormData.get(formtarget)?.value || [];
        let exists = currentCategories.find(c => c === catValue.value);
        if (!exists) {
            catValue.selected = true;
            currentCategories.push(catValue.value);
        } else {
            catValue.selected = false;
            currentCategories = currentCategories.filter(c => c !== catValue.value);
        }
        this.activityFormData.get(formtarget)?.setValue(currentCategories as any);
    }
    protected async submitActivity(): Promise<void> {
        let fd = this.activityFormData.value as unknown as ProcessingActivity;
        let result: boolean = false;
        console.log("Submitting Activity:", fd);
        if (fd.id) {
            // Update existing activity
            console.log("Update", fd);
            result = await this.gdprService.updateProcessingActivities(fd.id, fd);

        } else {
            // Create new activity
            console.log("Create", fd);
            result = await this.gdprService.createProcessingActivities(fd);
        }
    }

    protected cancel(): void {
        this.dialog.close();
    }
}
